// ============================================================================
// COURT FORM MODAL — Add / Edit Court (MSTR-COURT-001)
// ============================================================================
// Court Type is now a FK to mst.tbl_court_type (populated from Court Type Master).
// Court Place FK uses seedOptions cascade for inactive parents.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { courtService } from 'api/services/courtService';
import type { Court } from 'api/types/court.types';

type CourtStatus = 'ACTIVE' | 'INACTIVE';

interface CourtFormData {
  courtCode: string;
  courtName: string;
  courtTypeId: string;
  courtPlaceId: string;
  statusCode: CourtStatus;
}

const INITIAL_FORM_DATA: CourtFormData = {
  courtCode: '',
  courtName: '',
  courtTypeId: '',
  courtPlaceId: '',
  statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface CourtTypeOption {
  value: string;
  label: string;
}

export interface CourtPlaceOption {
  value: string;
  label: string;
}

export interface CourtFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: Court | null;
  /** Active court types for the dropdown. */
  courtTypeOptions: CourtTypeOption[];
  /** Seeded label for an inactive court type when editing. */
  seedCourtTypeOptions?: CourtTypeOption[];
  /** Active court places for the dropdown. */
  courtPlaceOptions: CourtPlaceOption[];
  /** Seeded label for an inactive/deleted court place when editing. */
  seedCourtPlaceOptions?: CourtPlaceOption[];
  onSaved: () => void;
  onClose: () => void;

  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const CourtFormModal: React.FC<CourtFormModalProps> = ({
  isOpen, mode, record,
  courtTypeOptions, seedCourtTypeOptions,
  courtPlaceOptions, seedCourtPlaceOptions,
  onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const combinedTypeOptions = useMemo(() => {
    if (!seedCourtTypeOptions || seedCourtTypeOptions.length === 0) return courtTypeOptions;
    const seedIds = new Set(seedCourtTypeOptions.map(s => s.value));
    return [
      ...courtTypeOptions.filter(o => !seedIds.has(o.value)),
      ...seedCourtTypeOptions,
    ];
  }, [courtTypeOptions, seedCourtTypeOptions]);

  const combinedPlaceOptions = useMemo(() => {
    const blank = [{ value: '', label: '— No place —' }];
    if (!seedCourtPlaceOptions || seedCourtPlaceOptions.length === 0) {
      return [...blank, ...courtPlaceOptions];
    }
    const seedIds = new Set(seedCourtPlaceOptions.map(s => s.value));
    return [
      ...blank,
      ...courtPlaceOptions.filter(o => !seedIds.has(o.value)),
      ...seedCourtPlaceOptions,
    ];
  }, [courtPlaceOptions, seedCourtPlaceOptions]);

  const [formData, setFormData] = useState<CourtFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => { if (!p[f]) return p; const n = { ...p }; delete n[f]; return n; });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const rec = recordRef.current;
    const data: CourtFormData = rec
      ? {
          courtCode: rec.courtCode,
          courtName: rec.courtName,
          courtTypeId: rec.courtTypeId,
          courtPlaceId: rec.courtPlaceId ?? '',
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as CourtStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.courtCode || currentFormData.courtCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.courtCode?.trim();
      const name = currentFormData.courtName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.courtCode || currentRecord?.courtCode || 'Court'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.courtCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.courtCode.trim();
    const name = formData.courtName.trim();
    if (!code) errors.courtCode = 'Court code is required';
    else if (code.length > 30) errors.courtCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.courtCode = 'Uppercase letters, digits, hyphens only';
    if (!name) errors.courtName = 'Court name is required';
    else if (name.length > 200) errors.courtName = 'Max 200 characters';
    if (!formData.courtTypeId) errors.courtTypeId = 'Court type is required';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.querySelector<HTMLInputElement>('input')?.focus(); }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.courtCode.trim().toUpperCase();
      const name = formData.courtName.trim();
      const courtPlaceId = formData.courtPlaceId || null;
      if (mode === 'create' || mode === 'copy') {
        await courtService.create({
          courtCode: code, courtName: name,
          courtTypeId: formData.courtTypeId, courtPlaceId,
          statusCode: formData.statusCode,
        });
        notificationService.toast.success(`Court "${code}" created successfully.`);
      } else if (record) {
        await courtService.update(record.id, {
          courtCode: code, courtName: name,
          courtTypeId: formData.courtTypeId, courtPlaceId,
          statusCode: formData.statusCode, rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Court "${code}" updated successfully.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This court was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A court with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save court. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => { setDiscardConfirmOpen(false); onClose(); }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Court' : mode === 'copy' ? 'Copy Court' : mode === 'create' ? 'Add Court' : 'Edit Court'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-group" data-field="courtCode">
            <label className="form-label">Court Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('courtCode') ? ' form-field-error' : ''}`}
              value={formData.courtCode}
              maxLength={30}
              placeholder="DRT-MUM-1, NCLT-DEL..."
              onChange={(e) => { clearFieldError('courtCode'); setFormData(p => ({ ...p, courtCode: e.target.value.toUpperCase() })); }}
              disabled={isSaving}
            />
            {getFieldError('courtCode') && <div className="form-error-message">{getFieldError('courtCode')}</div>}
          </div>

          <div className="form-group" data-field="courtName">
            <label className="form-label">Court Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('courtName') ? ' form-field-error' : ''}`}
              value={formData.courtName}
              maxLength={200}
              placeholder="DRT Mumbai Bench I"
              onChange={(e) => { clearFieldError('courtName'); setFormData(p => ({ ...p, courtName: e.target.value })); }}
              disabled={isSaving}
            />
            {getFieldError('courtName') && <div className="form-error-message">{getFieldError('courtName')}</div>}
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="courtTypeId">
              <SyutSelect
                label={<>Court Type <span className="text-danger">*</span></>}
                required
                options={combinedTypeOptions}
                value={formData.courtTypeId}
                onChange={(v) => { clearFieldError('courtTypeId'); setFormData(p => ({ ...p, courtTypeId: (v as string) || '' })); }}
                error={getFieldError('courtTypeId')}
                isClearable={false}
                placeholder="Select court type..."
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>

            <div className="form-group" data-field="courtPlaceId">
              <SyutSelect
                label="Court Place"
                options={combinedPlaceOptions}
                value={formData.courtPlaceId}
                onChange={(v) => { clearFieldError('courtPlaceId'); setFormData(p => ({ ...p, courtPlaceId: (v as string) || '' })); }}
                error={getFieldError('courtPlaceId')}
                isClearable={false}
                placeholder="— No place —"
                isDisabled={mode === 'view' || (isSaving)}
              />
            </div>
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as CourtStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create court "${formData.courtCode.trim().toUpperCase()}"?`
          : `Update court "${formData.courtCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CourtFormModal;
